/** The console's client state: what a screen is showing, plus the outcome panel a verb fills. */

import { create } from "zustand";

import { queryClient } from "@/lib/queryClient";

import type { CascadeResult, CascadeVerb } from "./lib/cascade";
import {
  actOnSpace,
  archiveSpace,
  restoreSpace,
  bindPosition,
  loadSpace,
  renameSpace,
  unloadSpace,
} from "./spaces/lib/verbs";
import type { AddedActivityRow, Candidate, LoadedSpace, SpaceRow, WireRow } from "./types";

function same(held: unknown, next: unknown): boolean {
  return JSON.stringify(held) === JSON.stringify(next);
}

function refresh(): void {
  void queryClient.invalidateQueries({ queryKey: ["spaces"] });
}

export interface ConsoleState {
  spaces: SpaceRow[];
  added: AddedActivityRow[];
  wires: WireRow[];
  loaded: LoadedSpace | null;
  cascade: CascadeResult | null;

  hydrateSpaces: (rows: SpaceRow[]) => void;
  hydrateAdded: (spaceId: string, rows: AddedActivityRow[]) => void;
  hydrateLoaded: (loaded: LoadedSpace | null) => void;

  load: (spaceId: string) => void;
  unload: () => void;
  runSpaceVerb: (spaceId: string, verb: CascadeVerb) => void;
  clearCascade: () => void;

  bind: (
    addedActivityId: string,
    requirement: string,
    position: number,
    candidate: Candidate,
  ) => void;
  rename: (spaceId: string, name: string) => void;
  archive: (spaceId: string) => void;
  restore: (spaceId: string) => void;
}

export const useConsole = create<ConsoleState>()((set, get) => ({
  spaces: [],
  added: [],
  wires: [],
  loaded: null,
  cascade: null,

  hydrateSpaces: (rows) => {
    const state = get();
    if (same(state.spaces, rows)) return;
    const held = rows.map((one) => one.space.space_id);
    set({ spaces: rows, added: state.added.filter((one) => held.includes(one.space_id)) });
  },

  hydrateAdded: (spaceId, rows) => {
    const state = get();
    const mine = state.added.filter((one) => one.space_id === spaceId);
    if (same(mine, rows)) return;
    set({ added: [...state.added.filter((one) => one.space_id !== spaceId), ...rows] });
  },

  hydrateLoaded: (loaded) => {
    if (same(get().loaded, loaded)) return;
    set({ loaded });
  },

  load: (spaceId) => {
    void loadSpace(spaceId).then(refresh, refresh);
  },

  unload: () => {
    const spaceId = get().loaded?.space_id;
    if (spaceId === undefined) return;
    void unloadSpace(spaceId).then(refresh, refresh);
  },

  runSpaceVerb: (spaceId, verb) => {
    const state = get();
    const row = state.spaces.find((one) => one.space.space_id === spaceId);
    const name = row === undefined ? spaceId : (row.space.name ?? row.space.join_code);
    const nameOf = (addedActivityId: string): string =>
      state.added.find((one) => one.added_activity_id === addedActivityId)?.name ?? "";

    void actOnSpace(spaceId, verb, nameOf).then(
      (activities) => {
        set({ cascade: { verb, space_id: spaceId, space_name: name, activities } });
        refresh();
      },
      (refusal: Error) => {
        set({
          cascade: {
            verb,
            space_id: spaceId,
            space_name: name,
            activities: [
              { added_activity_id: spaceId, name, succeeded: false, reason: refusal.message },
            ],
          },
        });
        refresh();
      },
    );
  },

  clearCascade: () => {
    set({ cascade: null });
  },

  bind: (addedActivityId, requirement, position, candidate) => {
    if (candidate.target_kind === null) return;
    void bindPosition(addedActivityId, {
      requirement_name: requirement,
      position,
      target_kind: candidate.target_kind,
      target_id: candidate.id,
    }).then(refresh, refresh);
  },

  rename: (spaceId, name) => {
    void renameSpace(spaceId, name).then(refresh, refresh);
  },

  archive: (spaceId) => {
    void archiveSpace(spaceId).then(refresh, refresh);
  },

  restore: (spaceId) => {
    void restoreSpace(spaceId).then(refresh, refresh);
  },
}));

/** One space by id. */
export function useSpace(spaceId: string): SpaceRow | undefined {
  return useConsole((state) => state.spaces.find((one) => one.space.space_id === spaceId));
}
